import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Star, Calendar } from 'lucide-react';
import { getLocalTodayDateString, getLocalDateStringFormatted } from '../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { CheckCircle2, Clock, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminTasks() {
  const { getAuthHeaders } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    points: 10,
    frequency: 'daily',
    gender_target: 'All',
    requires_approval: true,
    is_enabled: true,
    order: 0,
    deadline_days: 1,
    deadline_date: '',
    min_age: '',
    max_age: '',
    end_date: ''
  });

  const fetchTasks = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/tasks/all`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setTasks(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Prepare data - only include deadline_date for one_time tasks
    const submitData = { ...formData };
    if (submitData.frequency === 'daily') {
      submitData.deadline_date = null;
    }
    if (submitData.min_age === '') submitData.min_age = null;
    if (submitData.max_age === '') submitData.max_age = null;
    if (submitData.end_date === '') submitData.end_date = null;

    try {
      const url = editingTask 
        ? `${API_URL}/api/tasks/${editingTask.task_id}`
        : `${API_URL}/api/tasks`;
      
      const response = await fetch(url, {
        method: editingTask ? 'PUT' : 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        toast.success(editingTask ? 'Task updated!' : 'Task created!');
        setDialogOpen(false);
        resetForm();
        fetchTasks();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save task');
      }
    } catch (error) {
      toast.error('Failed to save task');
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Task deleted!');
        fetchTasks();
      }
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const resetForm = () => {
    setEditingTask(null);
    setFormData({
      name: '',
      description: '',
      points: 10,
      frequency: 'daily',
      gender_target: 'All',
      requires_approval: true,
      is_enabled: true,
      order: 0,
      deadline_days: 1,
      deadline_date: '',
      min_age: '',
      max_age: '',
      end_date: ''
    });
  };

  const openEditDialog = (task) => {
    setEditingTask(task);
    setFormData({
      name: task.name,
      description: task.description,
      points: task.points,
      frequency: task.frequency,
      gender_target: task.gender_target,
      requires_approval: task.requires_approval,
      is_enabled: task.is_enabled,
      order: task.order,
      deadline_days: task.deadline_days,
      deadline_date: task.deadline_date || '',
      min_age: task.min_age ?? '',
      max_age: task.max_age ?? '',
      end_date: task.end_date || ''
    });
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#08513F] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#264653] font-outfit">Manage Tasks</h1>
            <p className="text-[#6B7280] mt-1">Create and manage Islamic challenges</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#08513F] hover:bg-[#064031] text-white rounded-full px-6"
                data-testid="add-task-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Task
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-[#264653] font-outfit">
                  {editingTask ? 'Edit Task' : 'Create New Task'}
                </DialogTitle>
                <DialogDescription>
                  {editingTask ? 'Update task details' : 'Add a new Islamic challenge'}
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Task Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Pray Fajr on time"
                    className="rounded-xl"
                    data-testid="task-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe the task..."
                    className="rounded-xl"
                    data-testid="task-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="points">Points</Label>
                    <Input
                      id="points"
                      type="number"
                      min="1"
                      value={formData.points}
                      onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 1 })}
                      className="rounded-xl"
                      data-testid="task-points-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select
                      value={formData.frequency}
                      onValueChange={(value) => setFormData({ ...formData, frequency: value })}
                    >
                      <SelectTrigger className="rounded-xl" data-testid="task-frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="one_time">One-Time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Deadline Date for One-Time Tasks */}
                {formData.frequency === 'one_time' && (
                  <div className="space-y-2">
                    <Label htmlFor="deadline_date">Deadline Date</Label>
                    <Input
                      id="deadline_date"
                      type="date"
                      value={formData.deadline_date}
                      onChange={(e) => setFormData({ ...formData, deadline_date: e.target.value })}
                      min={getLocalTodayDateString()}
                      className="rounded-xl"
                      data-testid="task-deadline-input"
                    />
                    <p className="text-xs text-[#6B7280]">Leave empty for no deadline</p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Target Gender</Label>
                  <Select
                    value={formData.gender_target}
                    onValueChange={(value) => setFormData({ ...formData, gender_target: value })}
                  >
                    <SelectTrigger className="rounded-xl" data-testid="task-gender-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="All">All</SelectItem>
                      <SelectItem value="Muslim">Muslim (Boys)</SelectItem>
                      <SelectItem value="Muslimah">Muslimah (Girls)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Minimum Age</Label>
                    <Input
                      type="number"
                      min="5"
                      max="18"
                      placeholder="e.g. 8"
                      value={formData.min_age}
                      onChange={(e) => setFormData({ ...formData, min_age: e.target.value })}
                      className="rounded-xl h-12"
                      data-testid="task-min-age-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Maximum Age</Label>
                    <Input
                      type="number"
                      min="5"
                      max="18"
                      placeholder="e.g. 15"
                      value={formData.max_age}
                      onChange={(e) => setFormData({ ...formData, max_age: e.target.value })}
                      className="rounded-xl h-12"
                      data-testid="task-max-age-input"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 border border-[#08513F]/10 rounded-xl">
                  <div>
                    <Label htmlFor="enable_end_date" className="cursor-pointer font-medium">
                      Set Expiration Date
                    </Label>
                    <p className="text-xs text-[#6B7280] mt-1">After this date, the task won't be available to parents.</p>
                  </div>
                  <Switch
                    id="enable_end_date"
                    checked={!!formData.end_date}
                    onCheckedChange={(checked) => setFormData({ ...formData, end_date: checked ? getLocalTodayDateString() : '' })}
                    data-testid="task-enable-end-date"
                  />
                </div>
                {formData.end_date && (
                  <div className="space-y-2">
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="rounded-xl h-12"
                      data-testid="task-end-date-input"
                    />
                  </div>
                )}

                <div className="flex items-center justify-between p-4 bg-[#FFFBEB] rounded-xl">
                  <div>
                    <Label htmlFor="requires_approval" className="cursor-pointer">
                      Requires Admin Approval
                    </Label>
                    <p className="text-xs text-[#6B7280] mt-1">
                      {formData.requires_approval 
                        ? 'Admin must approve before points are awarded' 
                        : 'Points awarded immediately upon submission'}
                    </p>
                  </div>
                  <Switch
                    id="requires_approval"
                    checked={formData.requires_approval}
                    onCheckedChange={(checked) => setFormData({ ...formData, requires_approval: checked })}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-[#FFFBEB] rounded-xl">
                  <Label htmlFor="is_enabled" className="cursor-pointer">
                    Task Enabled
                  </Label>
                  <Switch
                    id="is_enabled"
                    checked={formData.is_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_enabled: checked })}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#E76F51] hover:bg-[#D65A3C] text-white rounded-xl h-12"
                  data-testid="save-task-btn"
                >
                  {editingTask ? 'Update Task' : 'Create Task'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tasks List */}
        {tasks.length === 0 ? (
          <Card className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg p-12 text-center">
            <CheckCircle2 className="w-16 h-16 text-[#08513F]/30 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#264653] font-outfit mb-2">No Tasks Yet</h2>
            <p className="text-[#6B7280]">Create your first Islamic challenge!</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {tasks.map((task, index) => (
              <Card 
                key={task.task_id}
                className={`bg-white rounded-2xl border-l-4 shadow-md hover:shadow-lg transition-shadow animate-slide-up ${
                  task.is_enabled ? 'border-l-[#08513F]' : 'border-l-gray-300'
                }`}
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`task-card-${task.task_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-lg font-bold text-[#264653] font-outfit">
                          {task.name}
                        </h3>
                        {!task.is_enabled && (
                          <Badge variant="outline" className="text-gray-500">Disabled</Badge>
                        )}
                        {task.is_expired && (
                          <Badge variant="outline" className="text-red-500 border-red-300">
                            Expired
                          </Badge>
                        )}
                        <Badge className={`rounded-full ${
                          task.frequency === 'daily' 
                            ? 'bg-[#08513F]/10 text-[#08513F]'
                            : 'bg-[#D4AF37]/10 text-[#B5952F]'
                        }`}>
                          {task.frequency === 'daily' ? 'Daily' : 'One-Time'}
                        </Badge>
                        {task.gender_target !== 'All' && (
                          <Badge variant="outline">{task.gender_target}</Badge>
                        )}
                        {(task.min_age || task.max_age) && (
                          <Badge variant="outline" className="text-[#08513F] border-[#08513F]/30">
                            {task.min_age && task.max_age
                              ? `${task.min_age}-${task.max_age} yrs`
                              : task.min_age
                                ? `Min ${task.min_age} yrs`
                                : `Max ${task.max_age} yrs`}
                          </Badge>
                        )}
                        {task.requires_approval ? (
                          <Badge variant="outline" className="text-yellow-600 border-yellow-300">
                            <Clock className="w-3 h-3 mr-1" />
                            Needs Approval
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-green-600 border-green-300">
                            <Zap className="w-3 h-3 mr-1" />
                            Auto-Approved
                          </Badge>
                        )}
                      </div>
                      <p className="text-[#6B7280] mb-3">{task.description}</p>
                      <div className="flex items-center gap-4 flex-wrap">
                        <span className="flex items-center gap-1 text-[#D4AF37] font-semibold">
                          <Star className="w-4 h-4" />
                          {task.points} points
                        </span>
                        {task.frequency === 'one_time' && task.deadline_date && (
                          <span className="flex items-center gap-1 text-[#6B7280] text-sm">
                            <Calendar className="w-4 h-4" />
                            Deadline: {getLocalDateStringFormatted(task.deadline_date)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => openEditDialog(task)}
                        className="rounded-xl"
                        data-testid={`edit-task-${task.task_id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleDelete(task.task_id)}
                        className="rounded-xl text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`delete-task-${task.task_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
